import { SelectChangeEvent } from '@mui/material';
import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ContactDto,
    DispatchDto,
    DispatchReviewDto,
    FreightBillingLineItemDto,
    JobDto,
    LineItemBillingRatesDto,
    LineItemDto,
    OrderByType,
    OrderDto,
    OrderLineItemDto,
    OrderReviewDto,
    OrderReviewLineItemDto,
    OrderReviewLineItemMemoUpdateDto,
} from '../../../dtos';
import { AssignmentType } from '../../../dtos/generated/AssignmentType';
import { ReviewStatus } from '../../../dtos/generated/ReviewStatus';
import { useCreateBillingRatesFreightLineItemBillingRatesMutation, useGetMaterialQuery, useGetOrderOrderReviewQuery, useUpdateOrderReviewLineItemsMemosMutation } from '../../../store/generated/generatedApi';
import { emptyGuid, LineItemTypes, setOrderByRequirementValidation } from '../../../util';
import { FormValidationMethod, IFormFieldValidationConfig, IFormProps, createValidatorConfig, isNotBlank, runFormValidation } from '../../CoreLib/library';
import { useLineItemConverter } from '../../../util/useLineItemConverter';
import { useMaterialDuplicateChecker } from '../../../util/useMaterialDuplicateChecker';

export interface IOrderReviewFormValues {
    orderNumber?: number;
    orderDate: Date;
    clientId?: string;
    jobId: string;
    job?: JobDto;
    orderId?: string;
    order?: OrderDto;
    orderLineItems: OrderReviewLineItemDto[];
    isActive: boolean;
    poNumber: string;
    customerJobNumber?: string;
    isTaxable?: boolean;
    orderedByContact?: ContactDto;
    contact?: ContactDto;
}

export const DEFAULT_ORDER_REVIEW: OrderReviewDto = {
    id: emptyGuid,
    clientId: emptyGuid,
    jobId: emptyGuid,
    orderId: emptyGuid,
    orderNumber: undefined,
    orderDate: new Date(),
    isActive: true,
    createdOn: new Date(),
    orderLineItems: [],
    status: ReviewStatus.InReview,
    statusName: 'In Review',
    readyForBilling: false,
    sentToQuickBooks: false,
    poNumber: '',
    customerJobNumber: '',
    isTaxable: undefined,
};

const ORDER_REVIEW_FORM_VALIDATION_CONFIG = new Map<keyof IOrderReviewFormValues, IFormFieldValidationConfig>([
    ['orderDate', createValidatorConfig([isNotBlank], 'Order Date')],
]);

/** Sort order review line items by Order's line item order (orderLineItemNumber then createdOn) and normalize orderLineItemNumber (1, 2, 3, ...). */
function sortAndRenumberOrderReviewLineItems(items: OrderReviewLineItemDto[]): OrderReviewLineItemDto[] {
    const sorted = [...items].sort((a, b) => {
        const na = a.orderLineItemNumber ?? Number.MAX_SAFE_INTEGER;
        const nb = b.orderLineItemNumber ?? Number.MAX_SAFE_INTEGER;
        if (na !== nb) return na - nb;
        const ca = a.createdOn ? new Date(a.createdOn).getTime() : 0;
        const cb = b.createdOn ? new Date(b.createdOn).getTime() : 0;
        return ca - cb;
    });
    return sorted.map((item, i) => ({
        ...item,
        orderLineItemNumber: i + 1,
    }));
}

/** Server-backed changes to the same order review (e.g. restore) must re-run the init sync; id alone is not enough. */
function buildOrderReviewInitSyncFingerprint(initValues: OrderReviewDto | undefined, order: OrderDto | undefined): string | undefined {
    if (!initValues?.id) {
        return undefined;
    }
    const items = initValues.orderLineItems ?? [];
    const canceledLineCount = items.filter((li) => !!li.canceledOn).length;
    return [
        initValues.id,
        String(initValues.status),
        initValues.canceledOn ? String(initValues.canceledOn) : '',
        initValues.updatedOn ? String(initValues.updatedOn) : '',
        order?.canceledOn ? String(order.canceledOn) : '',
        String(items.length),
        String(canceledLineCount),
    ].join('|');
}

function buildOrderReviewLineItemConfig(lineItem: OrderReviewLineItemDto, formStatus: ReviewStatus): Map<keyof OrderReviewLineItemDto, IFormFieldValidationConfig> {
    var validationConfig = new Map<keyof OrderReviewLineItemDto, IFormFieldValidationConfig>();
    // Quantity needs the requirement just as much as Loads does: without it the row saves with a NULL
    // LoadOrQuantityReq and the Dispatch screen's Requirement column has nothing to show (RK-7255). A
    // review line can reach that state by clearing the input or by being added by hand, since
    // createNewOrderReviewLineItem starts at Order By Quantity with no requirement.
    // Only enforced while the review is InReview, the status where this data is actively being entered or
    // corrected. AwaitingInvoicing and Invoiced reviews stay editable/savable for unrelated fields (PO
    // number, memo) - see editDisabled/saveDisabled in OrderReviewForm.tsx - and legacy Quantity lines there
    // can have no requirement and no billing-rate quantity for the backfill migration to restore, so
    // requiring it there would block saving the review for a value nobody can supply.
    setOrderByRequirementValidation(validationConfig, lineItem, formStatus === ReviewStatus.InReview);
    validationConfig.set('poNumber', createValidatorConfig([isNotBlank], 'PO Number'));
    return validationConfig;
}

function isValidDate(value: any): { isValid: boolean; errorMessageBuilder: (fieldName: string) => string } {
    const isValid = !value || !isNaN(new Date(value).getTime());
    return {
        isValid,
        errorMessageBuilder: (fieldName: string) => `${fieldName} must be a valid date.`,
    };
}

function calculateRemainingOrderQuantity(orderLineItem: OrderReviewLineItemDto, excludeDispatchIdx?: number): number {
    const orderRequestedQuantity = orderLineItem.loadOrQuantityReq;
    if (orderRequestedQuantity === undefined) return 0;

    if (orderLineItem.lineItem?.type === LineItemTypes.Hourly) return 0;

    const otherDispatches = orderLineItem.dispatches.filter(
        (_: any, idx: number) => idx !== excludeDispatchIdx
    );

    const alreadyDispatchedQuantity = otherDispatches.reduce((sum: number, d: any) => {
        return sum + (d.haulQuantity || 0);
    }, 0);

    return Math.max(orderRequestedQuantity - alreadyDispatchedQuantity, 0);
}

/**
 * @param dispatchesAlreadyScopedToLineItem When true, the dispatches array is known to contain only
 * dispatches for this line item
 */
export function calculateInitialHaulQuantityForNewDispatch(
    orderLineItem: OrderLineItemDto | OrderReviewLineItemDto,
    dispatches: DispatchDto[] | DispatchReviewDto[],
    dispatchesAlreadyScopedToLineItem: boolean = false
): number | undefined {
    if (!orderLineItem) return 0;
    switch (orderLineItem.orderBy) {
        case OrderByType.Equipment:
            return undefined;
        case OrderByType.Quantity:
            const orderRequestedQuantity = orderLineItem.loadOrQuantityReq;
            if (orderRequestedQuantity === undefined) return 0;

            if (orderLineItem.lineItem?.type === LineItemTypes.Hourly) return 0;

            let alreadyDispatchedQuantity = 0;
            dispatches.forEach((d: any) => {
                if (dispatchesAlreadyScopedToLineItem || d.orderLineItemId === orderLineItem.id) {
                    alreadyDispatchedQuantity += d.haulQuantity || 0;
                }
            });

            return Math.max(orderRequestedQuantity - alreadyDispatchedQuantity, 0);
        case OrderByType.Loads:
            return 0;
    }
}

function haulQuantityValidator(dispatchIdx: number, orderLineItem: OrderReviewLineItemDto, haulQuantity?: number): FormValidationMethod {
    let remainingQuantity = calculateRemainingOrderQuantity(orderLineItem, dispatchIdx);
    remainingQuantity = Math.round(remainingQuantity * 10000) / 10000;

    if (haulQuantity && haulQuantity > remainingQuantity) {
        return () => ({
            isValid: false,
            errorMessageBuilder: (fieldName: string) => `${fieldName} exceeds the max quantity required for the line item: (${remainingQuantity} available).`,
        });
    }

    return () => ({
        isValid: true,
        errorMessageBuilder: () => ``,
    });
}

function isAfterStartDate(value: any, startDate?: Date): { isValid: boolean; errorMessageBuilder: (fieldName: string) => string } {
    const isValid = !value || (!isNaN(new Date(value).getTime()) && !!startDate && new Date(value) > new Date(startDate!));
    return {
        isValid,
        errorMessageBuilder: (fieldName: string) => `${fieldName} must be after Onsite Time.`,
    };
}

function buildDispatchValidationConfig(dispatch: DispatchReviewDto, dispatchIdx: number, orderLineItem: OrderReviewLineItemDto): Map<keyof DispatchReviewDto, IFormFieldValidationConfig> {
    var validationConfig = new Map<keyof DispatchReviewDto, IFormFieldValidationConfig>();
    if (dispatch.assignmentType === AssignmentType.Broker) {
        validationConfig.set('brokerId', createValidatorConfig([isNotBlank], 'Broker'));
    } else {
        validationConfig.set('driverId', createValidatorConfig([isNotBlank], 'Driver'));
        validationConfig.set('equipmentId', createValidatorConfig([isNotBlank], 'Equipment'));
    }
    validationConfig.set('yardTime', createValidatorConfig([isValidDate], 'Yard Time'));
    validationConfig.set('startDate', createValidatorConfig([isValidDate], 'Start Date'));
    // end date must be after start date
    validationConfig.set('endDate', createValidatorConfig([isValidDate, (value) => isAfterStartDate(value, dispatch.startDate)], 'End Time'));

    const hasHaulQuantity = orderLineItem.orderBy !== OrderByType.Equipment && !(orderLineItem.lineItem?.type === LineItemTypes.Hourly && orderLineItem.orderBy === OrderByType.Quantity);

    if (hasHaulQuantity) {
        validationConfig.set('haulQuantity', createValidatorConfig([haulQuantityValidator(dispatchIdx, orderLineItem, dispatch.haulQuantity)], 'Haul Quantity'));
    }
    return validationConfig;
}

function buildFreightBillingValidationConfig(freightBilling: FreightBillingLineItemDto): Map<keyof FreightBillingLineItemDto, IFormFieldValidationConfig> {
    var validationConfig = new Map<keyof FreightBillingLineItemDto, IFormFieldValidationConfig>();
    validationConfig.set('freightBillNumber', createValidatorConfig([isNotBlank], 'Freight Bill #'));
    if (freightBilling.lineItemType === LineItemTypes.Material || freightBilling.lineItemType === LineItemTypes.Dump) {
        validationConfig.set('quantity', createValidatorConfig([isNotBlank], 'Quantity'));
    }
    return validationConfig;
}

/**
 * RK-8309: an invoiced review is billing's to change, but Customer Service still needs to record memos on it
 * (e.g. a payment that arrives after invoicing). A user who can edit reviews but not billing gets a memo-only
 * form there: every other field is locked and Save sends just the memos.
 */
export function isMemoOnlyReviewEdit(status: ReviewStatus | undefined, canEditReview: boolean, canEditBilling: boolean): boolean {
    return status === ReviewStatus.Invoiced && canEditReview && !canEditBilling;
}

/** The memo updates for saved, uncanceled line items whose Order Memo or Dispatch/Billing Memo differs from what was loaded. */
export function buildChangedLineItemMemos(savedLineItems: OrderReviewLineItemDto[], currentLineItems: OrderReviewLineItemDto[]): OrderReviewLineItemMemoUpdateDto[] {
    // The memo endpoint rejects the whole batch if any line item in it is canceled, so those are left out.
    return currentLineItems
        .filter((lineItem) => !lineItem.canceledOn)
        .flatMap((lineItem) => {
            const savedLineItem = savedLineItems.find((x) => x.id === lineItem.id);
            if (!savedLineItem) {
                return [];
            }
            const driverMemo = lineItem.driverMemo ?? '';
            const dispatchBillingMemo = lineItem.dispatchBillingMemo ?? '';
            if (driverMemo === (savedLineItem.driverMemo ?? '') && dispatchBillingMemo === (savedLineItem.dispatchBillingMemo ?? '')) {
                return [];
            }
            return [{ id: lineItem.id, driverMemo, dispatchBillingMemo }];
        });
}

export type OrderReviewFormProps = IFormProps<OrderReviewDto> & { onPendingRefreshApplied?: () => void };

export function useOrderReviewForm(props: OrderReviewFormProps, jobId: string, orderId: string) {
    const navigate = useNavigate();
    const { save, cancel, initValues, onPendingRefreshApplied } = props;
    const { data: order } = useGetOrderOrderReviewQuery({ id: orderId! });
    const { convertQuoteLineItemToOrderReviewLineItem } = useLineItemConverter(order?.quote);
    const [getBillingRates] = useCreateBillingRatesFreightLineItemBillingRatesMutation();
    const [updateLineItemMemos, memoSaveResult] = useUpdateOrderReviewLineItemsMemosMutation();

    const [formOrderNumber, setFormOrderNumber] = useState(initValues?.orderNumber ?? order?.orderNumber);
    const [formIsActive, setFormIsActive] = useState(initValues?.isActive ?? true);
    const [formOrderDate, setFormOrderDate] = useState<Date | null | undefined>(
        initValues?.orderDate ? new Date(initValues?.orderDate) : new Date(order?.orderDate ?? new Date())
    );
    const [formOrderLineItems, setFormOrderLineItems] = useState<OrderReviewLineItemDto[]>([]);
    const [formStatus, setFormStatus] = useState<ReviewStatus>(initValues?.status ?? ReviewStatus.InReview);
    const [formPoNumber, setFormPoNumber] = useState(initValues?.poNumber ?? order?.poNumber ?? '');
    const [formJobNumber, setFormJobNumber] = useState(initValues?.customerJobNumber ?? order?.customerJobNumber ?? '');

    const [formContact, setFormContact] = useState<ContactDto | undefined>(initValues?.order?.contact ?? undefined);
    const [formOrderByContact, setFormOrderByContact] = useState<ContactDto | undefined>(initValues?.order?.orderedByContact ?? undefined);

    const [isChanged, setIsChanged] = useState(false);
    const [formIsTaxable, setFormIsTaxable] = useState(initValues?.isTaxable ?? order?.isTaxable ?? false);

    useEffect(() => {
        if (!initValues?.id && order?.orderReview) {
            navigate(`/job/${jobId}/order/${orderId}/orderReview/${order?.orderReview.id}`);
        }
    }, [initValues?.id, jobId, navigate, order, orderId]);

    const lastSyncedFingerprintRef = useRef<string | undefined>(undefined);
    const pendingRefreshRef = useRef(false);
    useEffect(() => {
        const fingerprint = buildOrderReviewInitSyncFingerprint(initValues, order);
        let didClearPendingRefresh = false;

        if (pendingRefreshRef.current) {
            if (props.isLoading) {
                return;
            }
            lastSyncedFingerprintRef.current = undefined;
            pendingRefreshRef.current = false;
            didClearPendingRefresh = true;
        } else if (fingerprint !== undefined && fingerprint === lastSyncedFingerprintRef.current) {
            return;
        }

        lastSyncedFingerprintRef.current = fingerprint;

        setFormOrderNumber(initValues?.orderNumber ?? order?.orderNumber);
        setFormIsActive(initValues?.isActive ?? true);
        setFormOrderDate(initValues?.orderDate ? new Date(initValues?.orderDate) : new Date(order?.orderDate ?? new Date()));
        setFormOrderByContact(initValues?.orderedByContact ?? order?.orderedByContact);
        setFormContact(initValues?.contact ?? order?.contact);
        setFormIsTaxable(initValues?.isTaxable ?? order?.isTaxable ?? false);
        setFormOrderLineItems(
            sortAndRenumberOrderReviewLineItems(
                initValues?.orderLineItems.map((x) => {
                    return {
                        ...x,

                    } as OrderReviewLineItemDto;
                }) ??
                // RK-7215: copy every active order line item into a new review, canceled ones included.
                // Dropping the canceled ones made them vanish from the review entirely (not grayed out —
                // absent), which renumbered the remaining lines out of step with the order. They are copied
                // as-is; the spread carries canceledOn/cancellationReason so the existing gray-out treatment
                // applies immediately, and OrderReviewWriter.Create re-stamps cancellation from the order on
                // save (the DTO deliberately does not round-trip those server-owned fields).
                // Soft-deleted lines stay excluded: nothing upstream filters them (neither
                // OrderReader.WithIncludes nor OrderDto.GetOrderLineItems), and before this change the
                // canceled filter hid them by accident.
                order?.orderLineItems.filter((x) => x.isActive).map((x) => {
                const isHourly = x.lineItem?.type === LineItemTypes.Hourly;

                return {
                    ...x,
                    poNumber: x.poNumber ?? order?.poNumber ?? '',
                    customerJobNumber: x.isCustomerJobNumberCustom ? x.customerJobNumber : x.lineItem?.isCustomerJobNumberCustom ? x.lineItem?.customerJobNumber : order?.customerJobNumber ?? '',
                    status: ReviewStatus.InReview,
                    yardTime: x.yardTime,
                    onsiteTime: x.onsiteTime,
                    description: x.lineItem?.description,
                    unitOfMeasure: x.lineItem?.unitOfMeasure,
                    id: emptyGuid,
                    // Lets OrderReviewWriter.Create pair this copy back to its order line exactly; line
                    // numbers can't, because the copies are renumbered to their position below.
                    sourceOrderLineItemId: x.id,
                    billingRates: {
                        ...x.billingRates,
                        id: emptyGuid,
                        isActive: true,
                        createdOn: new Date(),
                    } as LineItemBillingRatesDto,
                    dispatches:
                        x.dispatches?.map((y) => {
                            const d = {
                                ...y,
                                assignmentType: y.brokerId ? AssignmentType.Broker : AssignmentType.Driver,
                                id: emptyGuid,
                                orderLineItemId: undefined,
                                equipment: y.equipment,
                                equipmentId: y.equipmentId,
                                yardTime: y.yardTime,
                                haulToAddressLine1: y.haulToAddressLine1 ?? '',
                                haulToAddressLine2: y.haulToAddressLine2 ?? '',
                                haulToCity: y.haulToCity ?? '',
                                haulToState: y.haulToState ?? '',
                                haulToZipCode: y.haulToZipCode ?? '',
                                haulToCountry: y.haulToCountry ?? '',
                                haulToDirections: y.haulToDirections ?? '',
                                haulToRegion: y.haulToRegion,
                                haulToRegionId: y.haulToRegionId,
                                haulToSiteName: y.haulToSiteName ?? '',
                                haulFromAddressLine1: y.haulFromAddressLine1 ?? '',
                                haulFromAddressLine2: y.haulFromAddressLine2 ?? '',
                                haulFromCity: y.haulFromCity ?? '',
                                haulFromState: y.haulFromState ?? '',
                                haulFromZipCode: y.haulFromZipCode ?? '',
                                haulFromCountry: y.haulFromCountry ?? '',
                                haulFromDirections: y.haulFromDirections ?? '',
                                haulFromRegion: y.haulFromRegion,
                                haulFromRegionId: y.haulFromRegionId,
                                haulFromSiteName: y.haulFromSiteName,
                                driverComment: y.driverComment,
                                freightBillingLineItems: [],
                            } as DispatchReviewDto & { clientTempId?: string };
                            d.clientTempId = crypto.randomUUID();
                            return d;
                        }) ?? [],
                    isDispatched: false,
                    isInvoiced: false,
                    siteId: isHourly ? x.site?.id : x.lineItem?.siteId,
                    site: isHourly ? x.site : x.lineItem?.site,
                    zone: isHourly ? x.zone : x.lineItem?.zone,
                } as OrderReviewLineItemDto;
            }) ??
            []
            )
        );
        setFormStatus(initValues?.status ?? ReviewStatus.InReview);
        setFormPoNumber(initValues?.poNumber ?? order?.poNumber ?? '');
        setFormJobNumber(initValues?.customerJobNumber ?? order?.customerJobNumber ?? '');

        if (didClearPendingRefresh) {
            onPendingRefreshApplied?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- onPendingRefreshApplied intentionally omitted so effect does not re-run when form passes new callback
    }, [initValues, order, props.isLoading]);

    const currentFormValues = useMemo((): IOrderReviewFormValues => {
        return {
            jobId: jobId,
            orderId: orderId,
            isActive: formIsActive,
            orderNumber: formOrderNumber,
            orderDate: formOrderDate ?? new Date(),
            orderLineItems: formOrderLineItems,
            poNumber: formPoNumber,
            customerJobNumber: formJobNumber,
            isTaxable: formIsTaxable,
            orderedByContact: formOrderByContact,
            contact: formContact,
        };
    }, [
        jobId,
        orderId,
        formIsActive,
        formOrderNumber,
        formOrderDate,
        formOrderLineItems,
        formPoNumber,
        formJobNumber,
        formIsTaxable,
        formOrderByContact,
        formContact
    ]);

    const [baseFormErrors, setBaseFormErrors] = useState<Map<keyof IOrderReviewFormValues, string>>(new Map());
    const [lineItemErrors, setLineItemErrors] = useState<Map<number, Map<keyof OrderReviewLineItemDto, string>>>(new Map());
    const [dispatchErrors, setDispatchErrors] = useState<Map<number, Map<number, Map<keyof DispatchReviewDto, string>>>>(new Map());
    const [freightErrors, setFreightErrors] = useState<Map<number, Map<number, Map<number, Map<keyof FreightBillingLineItemDto, string>>>>>(new Map());

    const validateForm = useCallback(() => {
        var isValid = true;
        const { isValid: isBaseValid, errorMessages: baseErrorMessages } = runFormValidation<IOrderReviewFormValues>(
            currentFormValues,
            ORDER_REVIEW_FORM_VALIDATION_CONFIG
        );
        !isBaseValid && (isValid = false);
        setBaseFormErrors(baseErrorMessages);

        var lineItemValidationErrors: Map<number, Map<keyof OrderReviewLineItemDto, string>> = new Map();
        var dispatchValidationErrors: Map<number, Map<number, Map<keyof DispatchReviewDto, string>>> = new Map();
        var freightBillingValidationErrors: Map<number, Map<number, Map<number, Map<keyof FreightBillingLineItemDto, string>>>> = new Map();

        currentFormValues.orderLineItems.forEach((lineItem, lineItemIdx) => {
            if (lineItem.canceledOn) {
                lineItemValidationErrors.set(lineItemIdx, new Map());
                dispatchValidationErrors.set(lineItemIdx, new Map());
                freightBillingValidationErrors.set(lineItemIdx, new Map());
                return;
            }
            const validationConfig = buildOrderReviewLineItemConfig(lineItem, formStatus);
            const { isValid: isLineItemValid, errorMessages: lineItemErrorMessages } = runFormValidation<OrderReviewLineItemDto>(lineItem, validationConfig);
            !isLineItemValid && (isValid = false);
            lineItemValidationErrors.set(lineItemIdx, lineItemErrorMessages);

            var lineItemDispatchErrors: Map<number, Map<keyof DispatchReviewDto, string>> = new Map();
            var lineItemFreightBillingValidationErrors: Map<number, Map<number, Map<keyof FreightBillingLineItemDto, string>>> = new Map();
            lineItem.dispatches.forEach((dispatch, dispatchIdx) => {
                const dispatchValidationConfig = buildDispatchValidationConfig(dispatch, dispatchIdx, lineItem);
                const { isValid: isDispatchValid, errorMessages: dispatchErrorMessages } = runFormValidation<DispatchReviewDto>(
                    dispatch,
                    dispatchValidationConfig
                );
                !isDispatchValid && (isValid = false);
                lineItemDispatchErrors.set(dispatchIdx, dispatchErrorMessages);

                var dispatchFreightBillingValidationErrors: Map<number, Map<keyof FreightBillingLineItemDto, string>> = new Map();
                dispatch.freightBillingLineItems?.forEach((freightBillingLineItem, freightBillingIdx) => {
                    const freightBillingValidationConfig = buildFreightBillingValidationConfig(freightBillingLineItem);
                    const { isValid: isFreightBillingValid, errorMessages: freightBillingErrorMessages } = runFormValidation<FreightBillingLineItemDto>(
                        freightBillingLineItem,
                        freightBillingValidationConfig
                    );
                    !isFreightBillingValid && (isValid = false);
                    dispatchFreightBillingValidationErrors.set(freightBillingIdx, freightBillingErrorMessages);
                });
                lineItemFreightBillingValidationErrors.set(dispatchIdx, dispatchFreightBillingValidationErrors);
            });
            dispatchValidationErrors.set(lineItemIdx, lineItemDispatchErrors);
            freightBillingValidationErrors.set(lineItemIdx, lineItemFreightBillingValidationErrors);
        });
        setLineItemErrors(lineItemValidationErrors);
        setDispatchErrors(dispatchValidationErrors);
        setFreightErrors(freightBillingValidationErrors);

        return isValid;
    }, [currentFormValues, formStatus]);

    const isFormDirty = useCallback(() => {
        return isChanged;
    }, [isChanged]);

    const handleOrderDateChange = useCallback((value: Date | null | undefined) => {
        setFormOrderDate(value);
        setIsChanged(true);
    }, []);

    const handleContactChange = useCallback((value?: ContactDto | undefined) => {
        setFormContact(value);
        setIsChanged(true);
    }, []);

    const handleOrderedByContactChange = useCallback((value?: ContactDto) => {
        setFormOrderByContact(value);
        setIsChanged(true);
    }, []);

    const handleOrderLineItemsChange = useCallback((lineItems: React.SetStateAction<OrderReviewLineItemDto[]>) => {
        setFormOrderLineItems(lineItems);
        setIsChanged(true);
    }, []);

    const handleFormStatusChange = useCallback((event: SelectChangeEvent) => {
        setFormStatus(parseInt(event.target.value));
        setIsChanged(true);
    }, []);

    const handleRemoveDispatchReview = useCallback(
        (lineItemIndex: number, dispatchIndex: number) => {
            setFormOrderLineItems((prev) =>
                prev.map((lineItem, idx) =>
                    idx === lineItemIndex
                        ? { ...lineItem, dispatches: lineItem.dispatches.filter((_, dIdx) => dIdx !== dispatchIndex) }
                        : lineItem
                )
            );
            setIsChanged(true);
        },
        []
    );

    const handleIsTaxableChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsTaxable(isChecked);
        setIsChanged(true);
    }, []);

    const createNewOrderReviewLineItem = useCallback(
        async (quoteLineItem?: LineItemDto): Promise<OrderReviewLineItemDto> => {
            if (quoteLineItem) {
                const orderReview = currentFormValues as OrderReviewDto;

                return (await convertQuoteLineItemToOrderReviewLineItem(quoteLineItem, formOrderLineItems.length + 1, orderReview)) as OrderReviewLineItemDto;
            } else {
                const result = {
                    id: emptyGuid,
                    isActive: true,
                    createdOn: new Date(),
                    orderReviewId: undefined,
                    lineItemId: undefined,
                    equipmentTypeId: undefined,
                    description: '',
                    orderBy: OrderByType.Quantity,
                    loadOrQuantityReq: undefined,
                    equipmentRequired: undefined,
                    memo: '',
                    driverMemo: '',
                    dispatchMemo: '',
                    yardTime: undefined,
                    onsiteTime: undefined,
                    orderLineItemNumber: formOrderLineItems.length + 1,
                    dispatches: [],
                    haulToAddressLine1: '',
                    haulToAddressLine2: '',
                    haulToCity: '',
                    haulToState: '',
                    haulToZipCode: '',
                    haulToCountry: '',
                    haulToDirections: '',
                    haulFromAddressLine1: '',
                    haulFromAddressLine2: '',
                    haulFromCity: '',
                    haulFromState: '',
                    haulFromZipCode: '',
                    haulFromCountry: '',
                    haulFromDirections: '',
                    haulToRegionId: undefined,
                    haulToRegion: undefined,
                    haulFromRegionId: undefined,
                    haulFromRegion: undefined,
                    haulToSiteName: '',
                    haulFromSiteName: '',
                    billingRates: {
                        id: emptyGuid,
                        isActive: true,
                        createdOn: new Date(),
                        lineItemRate: undefined,
                    },
                    isDispatched: false,
                    isInvoiced: false,
                    isCustomPoNumber: false,
                    isCustomerJobNumberCustom: false,
                    isCancelable: true,
                    poNumber: initValues?.poNumber ?? order?.poNumber ?? '',
                    customerJobNumber: initValues?.customerJobNumber ?? order?.customerJobNumber ?? '',
                    siteId: undefined,
                } as OrderReviewLineItemDto;
                (result as OrderReviewLineItemDto & { clientTempId?: string }).clientTempId = crypto.randomUUID();
                return result;
            }
        },
        [
            convertQuoteLineItemToOrderReviewLineItem,
            formOrderLineItems.length,
            initValues?.customerJobNumber,
            initValues?.poNumber,
            order?.customerJobNumber,
            order?.poNumber,
            currentFormValues,
        ]
    );

    const createNewDispatchReview = useCallback(
        (lineItemIndex: number) => {
            const lineItem = formOrderLineItems[lineItemIndex];
            const defaultHaulQuantity = calculateInitialHaulQuantityForNewDispatch(lineItem, lineItem.dispatches, true);
            const result = {
                assignmentType: AssignmentType.Broker,
                id: emptyGuid,
                isActive: true,
                createdOn: new Date(),
                orderLineItemId: undefined,
                driverId: undefined,
                brokerId: undefined,
                startDate: formOrderLineItems[lineItemIndex].onsiteTime,
                endDate: undefined,
                yardTime: formOrderLineItems[lineItemIndex].yardTime,
                equipmentId: undefined,
                haulToAddressLine1: formOrderLineItems[lineItemIndex].haulToAddressLine1 ?? '',
                haulToAddressLine2: formOrderLineItems[lineItemIndex].haulToAddressLine2 ?? '',
                haulToCity: formOrderLineItems[lineItemIndex].haulToCity ?? '',
                haulToState: formOrderLineItems[lineItemIndex].haulToState ?? '',
                haulToZipCode: formOrderLineItems[lineItemIndex].haulToZipCode ?? '',
                haulToCountry: formOrderLineItems[lineItemIndex].haulToCountry ?? '',
                haulToDirections: formOrderLineItems[lineItemIndex].haulToDirections ?? '',
                haulToRegion: formOrderLineItems[lineItemIndex].haulToRegion,
                haulToRegionId: formOrderLineItems[lineItemIndex].haulToRegionId,
                haulToSiteName: formOrderLineItems[lineItemIndex].haulToSiteName,
                haulFromAddressLine1: formOrderLineItems[lineItemIndex].haulFromAddressLine1 ?? '',
                haulFromAddressLine2: formOrderLineItems[lineItemIndex].haulFromAddressLine2 ?? '',
                haulFromCity: formOrderLineItems[lineItemIndex].haulFromCity ?? '',
                haulFromState: formOrderLineItems[lineItemIndex].haulFromState ?? '',
                haulFromZipCode: formOrderLineItems[lineItemIndex].haulFromZipCode ?? '',
                haulFromCountry: formOrderLineItems[lineItemIndex].haulFromCountry ?? '',
                haulFromDirections: formOrderLineItems[lineItemIndex].haulFromDirections ?? '',
                haulFromRegion: formOrderLineItems[lineItemIndex].haulFromRegion,
                haulFromRegionId: formOrderLineItems[lineItemIndex].haulFromRegionId,
                haulFromSiteName: formOrderLineItems[lineItemIndex].haulFromSiteName,
                driverComment: '',
                haulQuantity: defaultHaulQuantity,
                freightBillingLineItems: [],
            } as DispatchReviewDto & { clientTempId?: string };
            result.clientTempId = crypto.randomUUID();
            return result;
        },
        [formOrderLineItems]
    );

    const calculateBillingRates = useCallback(
        (billingRates: FreightBillingLineItemDto): Promise<FreightBillingLineItemDto> => {
            return getBillingRates(billingRates).then((response) => {
                return (response as any).data as FreightBillingLineItemDto;
            });
        },
        [getBillingRates]
    );

    const handleAddNewFreightBillingLineItem = useCallback(
        async (lineItemIndex: number, dispatchIndex: number) => {
            let freightBillingLineItem = {
                ...formOrderLineItems[lineItemIndex].billingRates,
                id: emptyGuid,
                isActive: true,
                createdOn: new Date(),
            };
            freightBillingLineItem = await calculateBillingRates(freightBillingLineItem);
            setFormOrderLineItems((prev) => {
                return prev.map((x, idx) => {
                    if (idx === lineItemIndex) {
                        return {
                            ...x,
                            dispatches: x.dispatches.map((y, dIdx) => {
                                if (dIdx === dispatchIndex) {
                                    return {
                                        ...y,
                                        freightBillingLineItems: y.freightBillingLineItems ? [...y.freightBillingLineItems, freightBillingLineItem] : [freightBillingLineItem],
                                    };
                                }
                                return y;
                            }),
                        };
                    }
                    return x;
                });
            });
            setIsChanged(true);
        },
        [calculateBillingRates, formOrderLineItems]
    );

    const handleRemoveFreightBillingLineItem = useCallback(
        (lineItemIndex: number, dispatchIndex: number, freightBillingLineItemIndex: number) => {
            setFormOrderLineItems((prev) =>
                prev.map((lineItem, liIdx) =>
                    liIdx !== lineItemIndex
                        ? lineItem
                        : {
                              ...lineItem,
                              dispatches: lineItem.dispatches.map((disp, dIdx) =>
                                  dIdx !== dispatchIndex
                                      ? disp
                                      : {
                                            ...disp,
                                            freightBillingLineItems: (disp.freightBillingLineItems ?? []).filter(
                                                (_, fbIdx) => fbIdx !== freightBillingLineItemIndex
                                            ),
                                        }
                              ),
                          }
                )
            );
            setIsChanged(true);
        },
        []
    );

    const handleAddNewOrderReviewLineItem = useCallback(
        async (quoteLineItem?: LineItemDto) => {
            var newOrderReviewLineItem = await createNewOrderReviewLineItem(quoteLineItem);
            setFormOrderLineItems((current) => sortAndRenumberOrderReviewLineItems([...current, newOrderReviewLineItem]));
            setIsChanged(true);
        },
        [createNewOrderReviewLineItem]
    );

    const convertMaterialLineItemToOrderReviewLineItem = useCallback(
        async (materialLineItem: LineItemDto, lineItemNumber: number): Promise<OrderReviewLineItemDto> => {
            const orderReview = currentFormValues as OrderReviewDto;
            return await convertQuoteLineItemToOrderReviewLineItem(materialLineItem, lineItemNumber, orderReview);
        },
        [convertQuoteLineItemToOrderReviewLineItem, currentFormValues]
    );

    // Get materials to look up materialId by code (since materialId is not always in the lineItem structure)
    const { data: materialsData } = useGetMaterialQuery({
        sorting: ['CODE+'],
        page: 0,
        pageSize: 10000,
        includeInactive: false
    });

    // Use reusable hook for duplicate checking
    const { checkForDuplicateMaterial, getExcludedMaterialIdsForHaulingLineItem } = useMaterialDuplicateChecker({
        lineItems: formOrderLineItems,
        materialsData,
    });

    const handleRemoveOrderReviewLineItem = useCallback(
        (lineItemIndex: number) => {
            const updatedLineItems = formOrderLineItems.filter((_, i) => i !== lineItemIndex);
            setFormOrderLineItems(sortAndRenumberOrderReviewLineItems(updatedLineItems));
            setIsChanged(true);
        },
        [formOrderLineItems]
    );

    const handleAddNewDispatchReview = useCallback(
        (lineItemIndex: number) => {
            var updatedLineItems = _.cloneDeep(formOrderLineItems);
            updatedLineItems[lineItemIndex].dispatches.push(createNewDispatchReview(lineItemIndex));
            setFormOrderLineItems(updatedLineItems);
            setIsChanged(true);
        },
        [createNewDispatchReview, formOrderLineItems]
    );

    const isFormValid = useMemo(() => {
        return validateForm();
    }, [validateForm]);

    const handleSave = useCallback(() => {
        if (isFormValid) {
            const updatedOrderReviewRecord = {
                ...(initValues ?? DEFAULT_ORDER_REVIEW),
                ...currentFormValues,
                contactId: currentFormValues.contact?.id,
                orderedByContactId: currentFormValues.orderedByContact?.id,
                sentToQuickBooks: undefined,
            };
            pendingRefreshRef.current = true;
            save(updatedOrderReviewRecord);
            setIsChanged(false);
        }
    }, [currentFormValues, initValues, save, isFormValid]);

    const handleSaveMemos = useCallback(() => {
        const memoUpdates = buildChangedLineItemMemos(initValues?.orderLineItems ?? [], formOrderLineItems);
        setIsChanged(false);
        if (memoUpdates.length === 0) {
            return;
        }
        // Saving invalidates the OrderReview cache, so the refetch resyncs the form through pendingRefreshRef.
        pendingRefreshRef.current = true;
        updateLineItemMemos(memoUpdates)
            .unwrap()
            .catch(() => {
                pendingRefreshRef.current = false;
                setIsChanged(true);
            });
    }, [formOrderLineItems, initValues?.orderLineItems, updateLineItemMemos]);

    const handleSubmit = useCallback(
        (sentToQuickBooks: boolean) => {
            if (!isFormValid) {
                return;
            }

            const updatedUserRecord = {
                ...(initValues ?? DEFAULT_ORDER_REVIEW),
                ...currentFormValues,
                contactId: currentFormValues.contact?.id,
                orderedByContactId: currentFormValues.orderedByContact?.id,
                readyForBilling: true,
                sentToQuickBooks,
            };

            pendingRefreshRef.current = true;
            setIsChanged(false);
            return save(updatedUserRecord);
        },
        [currentFormValues, initValues, isFormValid, save]
    );

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    const handlePoNumberChange = useCallback(
        (value: string) => {
            setFormPoNumber(value);
            // Update all line items with isCustomPoNumber set to false with the new PO number
            const updatedLineItems = formOrderLineItems.map((x) => {
                if (!x.isCustomPoNumber) {
                    return {
                        ...x,
                        poNumber: value,
                    };
                }
                return x;
            });
            setFormOrderLineItems(updatedLineItems);
        },
        [formOrderLineItems]
    );

    const handleJobNumberChange = useCallback(
        (value: string) => {
            setFormJobNumber(value);
            // Update all line items with isCustomJobNumber set to false with the new Job number
            const updatedLineItems = formOrderLineItems.map((x) => {
                if (!x.isCustomerJobNumberCustom) {
                    return {
                        ...x,
                        customerJobNumber: value,
                    };
                }
                return x;
            });
            setFormOrderLineItems(updatedLineItems);
        },
        [formOrderLineItems]
    );

    return {
        isFormDirty,
        isFormValid,
        handleSave,
        handleSaveMemos,
        memoSaveResult,
        handleSubmit,
        handleCancel,
        baseFormErrors,
        lineItemErrors,
        dispatchErrors,
        freightErrors,
        handleOrderDateChange,
        handleContactChange,
        handleOrderedByContactChange,
        handleOrderLineItemsChange,
        handleAddNewDispatchReview,
        handleAddNewOrderReviewLineItem,
        handleAddNewFreightBillingLineItem,
        handleRemoveDispatchReview,
        handleRemoveOrderReviewLineItem,
        handleRemoveFreightBillingLineItem,
        handleFormStatusChange,
        handleIsTaxableChange,
        handlePoNumberChange,
        handleJobNumberChange,
        convertMaterialLineItemToOrderReviewLineItem,
        checkForDuplicateMaterial,
        getExcludedMaterialIdsForHaulingLineItem,
        formIsActive,
        formOrderNumber,
        formOrderDate,
        formOrderByContact,
        formContact,
        formOrderLineItems,
        formStatus,
        formPoNumber,
        formJobNumber,
        formIsTaxable,
        order,
    };
}

export function isErrorInMap(errors: Map<string, string> | undefined): boolean {
    if (!errors) {
        return false;
    }
    return Array.from(errors.values()).some((x) => x !== '');
}
